import { Controller } from "@hotwired/stimulus"

/**
 * Generate Progress Controller
 *
 * Shows real progress indicator when generating AI recommendations.
 * Polls the backend for actual job status and updates the UI accordingly.
 *
 * Usage:
 *   <div data-controller="generate-progress"
 *        data-generate-progress-poll-url-value="/analytics/recommendations/job_status">
 *     <form data-action="submit->generate-progress#start">
 *       <button data-generate-progress-target="button">Generate</button>
 *     </form>
 *     <div data-generate-progress-target="card" class="progress-card">
 *       <div data-generate-progress-target="bar" class="progress-bar"></div>
 *       <div data-generate-progress-target="step">Step 1</div>
 *       <div data-generate-progress-target="step">Step 2</div>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["card", "bar", "step", "button"]
  static values = {
    pollUrl: { type: String, default: "" },
    pollInterval: { type: Number, default: 1500 },
    maxPollAttempts: { type: Number, default: 60 }, // 90 seconds max
    minDisplayTime: { type: Number, default: 2000 } // Minimum time to show progress
  }

  connect() {
    this.currentStep = 0
    this.pollTimer = null
    this.pollAttempts = 0
    this.formSubmitted = false
    this.jobId = null
    this.startTime = null
  }

  disconnect() {
    this.stopPolling()
  }

  async start(event) {
    // Prevent default form submission
    event.preventDefault()

    // Store form reference
    const form = event.target

    // Prevent double submission
    if (this.formSubmitted) return
    this.formSubmitted = true
    this.startTime = Date.now()

    // Add loading state to button
    this.buttonTargets.forEach(btn => {
      btn.classList.add('loading')
      btn.disabled = true
    })

    // Show progress card
    if (this.hasCardTarget) {
      this.cardTarget.classList.add('active')
      this.cardTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    // Reset state and show initial step
    this.resetSteps()
    this.currentStep = 0
    this.advanceToStep(1) // Start at step 1

    try {
      // Submit form via AJAX to get job_id
      const formData = new FormData(form)
      const response = await fetch(form.action, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || ''
        },
        body: formData
      })

      const data = await response.json()

      if (data.success) {
        if (data.job_id && data.ai_enabled) {
          // AI job started - poll for real status
          this.jobId = data.job_id
          this.redirectUrl = data.redirect_url
          this.startPolling()
        } else {
          // No AI job (v1 only or AI disabled) - show quick progress and redirect
          await this.showQuickProgress(data.redirect_url)
        }
      } else {
        // Error - fallback to regular form submission
        this.showError()
        setTimeout(() => form.submit(), 500)
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      // Fallback to regular form submission
      form.submit()
    }
  }

  async showQuickProgress(redirectUrl) {
    // Show progress through all steps quickly (v1 recommendations are fast)
    const stepDelay = 400

    for (let i = 2; i <= this.stepTargets.length; i++) {
      await this.sleep(stepDelay)
      this.advanceToStep(i)
    }

    // Ensure minimum display time has passed
    const elapsed = Date.now() - this.startTime
    if (elapsed < this.minDisplayTimeValue) {
      await this.sleep(this.minDisplayTimeValue - elapsed)
    }

    // Complete and redirect
    this.completeProgress()
    await this.sleep(300)
    window.location.href = redirectUrl
  }

  startPolling() {
    this.pollAttempts = 0
    this.pollTimer = setInterval(() => this.pollJobStatus(), this.pollIntervalValue)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  async pollJobStatus() {
    this.pollAttempts++

    // Safety limit to prevent infinite polling
    if (this.pollAttempts >= this.maxPollAttemptsValue) {
      this.stopPolling()
      this.handleTimeout()
      return
    }

    try {
      // Build poll URL - use data attribute or construct from job_id
      const pollUrl = this.pollUrlValue || this.buildPollUrl()
      const response = await fetch(`${pollUrl}/${this.jobId}`, {
        headers: {
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      // Update progress based on real job status
      this.updateProgress(data)

      // Check if job is complete
      if (data.status === 'completed' || data.status === 'failed') {
        this.stopPolling()
        await this.handleJobComplete(data)
      }
    } catch (error) {
      console.error('Error polling job status:', error)
      // Continue polling - might be temporary network issue
    }
  }

  buildPollUrl() {
    // Try to extract base URL from current location
    const pathParts = window.location.pathname.split('/')
    // Find "recommendations" in the path and construct job_status URL
    const recIndex = pathParts.indexOf('recommendations')
    if (recIndex > 0) {
      return pathParts.slice(0, recIndex + 1).join('/') + '/job_status'
    }
    // Fallback
    return '/organization_owner/analytics/recommendations/job_status'
  }

  updateProgress(data) {
    const step = data.step || 1
    const progress = data.progress || 0

    // Update progress bar
    if (this.hasBarTarget) {
      this.barTarget.style.width = `${Math.min(progress, 100)}%`
    }

    // Advance to the current step
    this.advanceToStep(step)
  }

  advanceToStep(targetStep) {
    const totalSteps = this.stepTargets.length

    // Mark all previous steps as completed
    for (let i = 0; i < totalSteps; i++) {
      const step = this.stepTargets[i]
      const stepNumber = i + 1

      if (stepNumber < targetStep) {
        step.classList.remove('active')
        step.classList.add('completed')
      } else if (stepNumber === targetStep) {
        step.classList.remove('completed')
        step.classList.add('active')
      } else {
        step.classList.remove('active', 'completed')
      }
    }

    // Update progress bar based on step
    const progress = Math.min((targetStep / totalSteps) * 100, 100)
    if (this.hasBarTarget) {
      this.barTarget.style.width = `${progress}%`
    }

    this.currentStep = targetStep
  }

  async handleJobComplete(data) {
    // Ensure minimum display time
    const elapsed = Date.now() - this.startTime
    if (elapsed < this.minDisplayTimeValue) {
      await this.sleep(this.minDisplayTimeValue - elapsed)
    }

    // Complete all steps
    this.completeProgress()

    // Brief pause to show completion
    await this.sleep(500)

    // Redirect to results
    window.location.href = this.redirectUrl || window.location.pathname
  }

  handleTimeout() {
    // Job took too long - complete anyway and redirect
    // V1 recommendations should already be generated
    this.completeProgress()
    setTimeout(() => {
      window.location.href = this.redirectUrl || window.location.pathname
    }, 500)
  }

  completeProgress() {
    // Mark all steps as completed
    this.stepTargets.forEach(step => {
      step.classList.remove('active')
      step.classList.add('completed')
    })

    // Fill progress bar
    if (this.hasBarTarget) {
      this.barTarget.style.width = '100%'
    }
  }

  showError() {
    // Could add error styling here if needed
    console.error('Generation failed')
  }

  resetSteps() {
    this.stepTargets.forEach(step => {
      step.classList.remove('active', 'completed')
    })
    if (this.hasBarTarget) {
      this.barTarget.style.width = '0%'
    }
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}
